import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/LanguageSwitcher.css";

// List of available languages
const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", shortLabel: "EN" },
  { code: "ha", label: "Hausa", shortLabel: "HA" },
];

function LanguageSwitcher({ variant = "floating" } = {}) {
  // variant:
  // It is used to control how the LanguageSwitcher looks and where it is displayed on the page.
  // i.e like a floating button or a navbar item.
  // "floating" → default style (usually positioned freely on the page)
  // "navbar"   → styled to fit inside a navigation bar

  const { i18n } = useTranslation(); // Gets access to the translation engine
  const [isOpen, setIsOpen] = useState(false); // It controls whether the language menu is visible.
  const switcherRef = useRef(null); // Reference to the language switcher element

  // Finds the currently active language
  const activeLanguage =
    LANGUAGE_OPTIONS.find((option) =>
      // Checks if the current language starts with the language code e.g if the current language is "en", it checks if it starts with "en"
      i18n.resolvedLanguage?.toLowerCase().startsWith(option.code),
    ) ?? LANGUAGE_OPTIONS[0]; // If the active language is not found, it defaults to the first language

  // Detects when the user clicks outside the language menu
  useEffect(() => {
    // A function that runs when the user clicks outside the language menu.
    const handlePointerDown = (event) => {
      // Checks if the user clicked outside the language switcher, if yes then close the language menu
      if (switcherRef.current && !switcherRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Listens when the user clicks anywhere on the page, run the handlePointerDown function.
    document.addEventListener("mousedown", handlePointerDown);

    // Clean up the event listener to prevent memory leaks
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  // Handles language change i.e switches the language
  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode); // Changes the language
    setIsOpen(false); // Closes the language menu
  };

  return (
    <div
      className={`language-switcher${variant === "navbar" ? " language-switcher--navbar" : ""}`}
      ref={switcherRef}
    >
      {/* LANGUAGE SWITCHER BUTTON */}
      <button
        type="button"
        className="language-switcher-button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Change language"
        title="Change language"
      >
        <span className="language-switcher-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              d="M12 3a9 9 0 1 0 9 9 9.01 9.01 0 0 0-9-9Zm6.86 8h-2.95a14.8 14.8 0 0 0-1.21-4.34A7.04 7.04 0 0 1 18.86 11ZM12 5.05A12.9 12.9 0 0 1 13.89 11h-3.78A12.9 12.9 0 0 1 12 5.05ZM5.14 13h2.95a14.8 14.8 0 0 0 1.21 4.34A7.04 7.04 0 0 1 5.14 13ZM8.09 11H5.14A7.04 7.04 0 0 1 9.3 6.66 14.8 14.8 0 0 0 8.09 11ZM12 18.95A12.9 12.9 0 0 1 10.11 13h3.78A12.9 12.9 0 0 1 12 18.95ZM14.7 17.34A14.8 14.8 0 0 0 15.91 13h2.95A7.04 7.04 0 0 1 14.7 17.34Z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>

      {/* LANGUAGE MENU */}
      {isOpen && (
        <div className="language-switcher-menu" role="listbox" aria-label="Language options">
          {LANGUAGE_OPTIONS.map((option) => {
            // Checks if the option is the active language
            const isActive = activeLanguage.code === option.code;

            return (
              <button
                key={option.code}
                type="button"
                className={`language-switcher-option${isActive ? " is-active" : ""}`}
                onClick={() => handleLanguageChange(option.code)}
                role="option"
                aria-selected={isActive}
              >
                <span className="language-option-name">{option.label}</span>
                <span className="language-option-code">{option.shortLabel}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
